import { Box, Dialog, DialogTitle, DialogContent, DialogActions, DialogContentText, Typography } from '@mui/material';
import dayjs from 'dayjs';
import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { ApplicationStatus } from '../../api/types';
import { ApplicationSentModal } from '../../components/applicationSentModal';
import { Button } from '../../components/button';
import { CreateStep1V3 } from '../../components/createSteps/CreateStep1V3';
import { CreateStep2 } from '../../components/createSteps/createStep2';
import { auth } from '../../config/firebase';
import { findBlockingApplication, rentalPeriod } from '../../utils/rentalPhase';
import { useAPI } from '../../hooks/useAPI';
import { COLORS } from '../../theme';
import { formatPersonalNumber } from '../../utils/formatting';
// Validation är nu hanterad i CreateStep2 via onValidationChange

const Create = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(1);
  const [open, setOpen] = useState(false);
  const [applicationLoading, setApplicationLoading] = useState(false);
  const [showDateConfirmDialog, setShowDateConfirmDialog] = useState(false);
  const dateConfirmedRef = useRef(false);
  const sublettingReasons = [
    {
      title: 'reason_study',
      value: t('subletting_reason_study')
    },
    {
      title: 'reason_abroad',
      value: t('subletting_reason_abroad')
    },
    {
      title: 'reason_provsamboende',
      value: t('subletting_reason_sambo')
    },
    {
      title: 'reason_hospital',
      value: t('subletting_reason_hospital')
    },
    {
      title: 'reason_wait_sale',
      value: t('subletting_reason_sale')
    },
    {
      title: 'reason_relative',
      value: t('subletting_reason_relative')
    },
    {
      title: 'other_reason',
      value: t('subletting_reason_other')
    }
  ];

  const [values, setValues] = useState({
    fromDate: dayjs(),
    toDate: dayjs().add(1, 'year').subtract(1, 'day'),
    selectedAssociation: '',
    selectedApartment: '',
    address: '',
    city: '',
    zipCode: '',
    reason: sublettingReasons[0].value,
    tenantFirstName: '',
    tenantLastName: '',
    tenantPhoneNumber: '',
    tenantEmail: '',
    tenantEmailConfirm: '',
    tenantPersonalNumber: '',
    squareMeter: null,
    rooms: null,
    contractStartDate: dayjs(),
    contractEndDate: dayjs().add(1, 'year').subtract(1, 'day'),
    rentalFee: null,
    deposit: null,
    clearingnummer: '',
    bankAccountNumber: '',
    bankgiro: '',
    heatingIncluded: null,
    hotWaterIncluded: null,
    electricityIncluded: null,
    internetIncluded: null,
    parkingIncluded: null,
    furnitureIncluded: null,
    miscellaneousNotes: ''
  });
  const [otherReason, setOtherReason] = useState(null);
  const [housingList, setHousingList] = useState([]);
  const [associationsList, setAssociationsList] = useState([]);
  const [availableApartments, setAvailableApartments] = useState([]);

  // Step 2 validation state
  const [step2HasErrors, setStep2HasErrors] = useState(true);
  const [triggerStep2Validation, setTriggerStep2Validation] = useState(null);

  const {
    getHousingInfo,
    getApplications,
    makeApplication,
    makeApplicationDirectly,
    makeApplicationStepTwo,
    isLoading
  } = useAPI();
  const { state } = useLocation();

  useEffect(() => {
    if (values.reason !== sublettingReasons[6].value) {
      setOtherReason(null);
    }
  }, [values, sublettingReasons]);

  // Handle URL query parameters
  useEffect(() => {
    const stepParam = searchParams.get('step');
    const idParam = searchParams.get('id');

    if (stepParam) {
      const stepNumber = parseInt(stepParam);
      if (!isNaN(stepNumber)) {
        setStep(stepNumber);
      }
    }

    // Byte av hyresgäst går inte längre via guiden — se containers/changeTenant.
    if (idParam) {
      loadApplicationById(idParam);
    }
  }, [searchParams]);

  const loadApplicationById = async (applicationId) => {
    try {
      setApplicationLoading(true);
      const { doc, getDoc } = await import('firebase/firestore');
      const { db } = await import('../../config/firebase');

      const applicationDoc = await getDoc(doc(db, 'applications', applicationId));

      if (applicationDoc.exists()) {
        const applicationData = applicationDoc.data();

        // Set all the values from the existing application
        const fromDate = applicationData.fromDate ? dayjs(applicationData.fromDate) : dayjs();
        const toDate = applicationData.toDate ? dayjs(applicationData.toDate) : dayjs().add(1, 'year').subtract(1, 'day');

        setValues(prevState => ({
          ...prevState,
          ...applicationData,
          id: applicationDoc.id,
          // Convert date strings back to Dayjs objects if needed
          fromDate: fromDate,
          toDate: toDate,
          // Avtalsperiod: dagens datum till max tillåtet slutdatum (uthyrningsperiodens slut)
          contractStartDate: applicationData.contractStartDate ? dayjs(applicationData.contractStartDate) : dayjs(),
          contractEndDate: applicationData.contractEndDate ? dayjs(applicationData.contractEndDate) : toDate
        }));
      } else {
        console.error('Application not found with ID:', applicationId);
      }
    } catch (error) {
      console.error('Error loading application:', error);
    } finally {
      setApplicationLoading(false);
    }
  };

  useEffect(() => {
    auth.onAuthStateChanged(async (user) => {
      if (!user?.uid) return;

      const housingInfo = await getHousingInfo(user.uid);
      if (!housingInfo) return;
      // Alla ägarens lägenheter visas. Förr GÖMDES en lägenhet så fort den hade en
      // ansökan som inte var avslagen — även en avslutad uthyrning — så ägaren
      // kunde aldrig söka igen, och inte för perioden efter en pågående
      // uthyrning. Om PERIODEN krockar avgörs när ansökan skickas in.
      const filteredHousing = housingInfo;
      setHousingList(filteredHousing);
      setAvailableApartments(filteredHousing);

      // Extract unique housing cooperatives
      const uniqueAssociations = [...new Set(filteredHousing.map(h => h.housingCooperative).filter(Boolean))];
      setAssociationsList(uniqueAssociations);

      const { application } = state ?? {};
      if (application) {
        setValues(application);
        setStep(application.step);
        return;
      }

      // Only set housing data if we have at least one housing
      if (filteredHousing && filteredHousing.length > 0) {
        setValues((prevState) => ({
          ...prevState,
          address: filteredHousing[0].address,
          zipCode: filteredHousing[0].zipCode,
          city: filteredHousing[0].city
        }));
      }
    });
    // eslint-disable-next-line
  }, [state]);

  const handleChange = (name) => (value) => {
    // If value has a target property, it's an event, extract the value
    // Otherwise, it's a direct value (like Dayjs object from DatePicker)
    let val = value?.target?.value !== undefined ? value.target.value : value;

    if (name === 'tenantPersonalNumber') {
      val = formatPersonalNumber(val, values.tenantPersonalNumber);
    }

    // Auto-fill: När fromDate ändras, sätt automatiskt toDate till 1 år senare
    if (name === 'fromDate' && val) {
      const newFromDate = dayjs(val);
      const newToDate = newFromDate.add(1, 'year').subtract(1, 'day');

      setValues((prevState) => ({
        ...prevState,
        fromDate: val,
        toDate: newToDate
      }));
      return;
    }

    setValues((prevState) => ({
      ...prevState,
      [name]: val
    }));
  };

  const handleAssociationChange = (association) => {
    setValues((prevState) => ({
      ...prevState,
      selectedAssociation: association,
      selectedApartment: ''
    }));

    // Filter available apartments by selected association
    const filtered = housingList.filter(h => h.housingCooperative === association);
    setAvailableApartments(filtered);
  };

  // Handlers för datumbekräftelse-dialogen
  const handleDateConfirm = () => {
    setShowDateConfirmDialog(false);
    dateConfirmedRef.current = true;
    // Fortsätt med submit efter bekräftelse
    handleSubmit();
  };

  const handleDateCorrect = () => {
    setShowDateConfirmDialog(false);
    // Användaren kan nu korrigera datumen
  };

  // Reset dateConfirmed när datum ändras
  useEffect(() => {
    dateConfirmedRef.current = false;
  }, [values.fromDate, values.toDate]);

  const handleSubmit = async () => {
    if (step === 1) {
      // Kontrollera om uthyrningsperioden är under 180 dagar
      const daysDiff = dayjs(values.toDate).diff(dayjs(values.fromDate), 'day');

      if (daysDiff < 180 && !dateConfirmedRef.current) {
        setShowDateConfirmDialog(true);
        return; // Avbryt submit, vänta på bekräftelse
      }
      const housing = housingList.find(({ address }) =>
        values.address.includes(address)
      );

      if (!housing) {
        console.error('Housing not found for address:', values.address);
        return;
      }

      const reason =
        values.reason !== sublettingReasons[6].value
          ? values.reason
          : otherReason;

      // Log för debugging
      console.log('🔍 Auth state:', {
        uid: auth.currentUser?.uid,
        email: auth.currentUser?.email,
        isAuthenticated: !!auth.currentUser
      });
      console.log('🔍 Housing data:', housing);

      if (!auth.currentUser?.uid) {
        console.error('❌ Användaren är inte inloggad!');
        alert('Du måste vara inloggad för att skicka in ansökan.');
        return;
      }

      const applicationData = {
        ...values,
        reason,
        fromDate: dayjs(values.fromDate).format('YYYY-MM-DD'),
        toDate: dayjs(values.toDate).format('YYYY-MM-DD'),
        contractStartDate: dayjs(values.contractStartDate).format('YYYY-MM-DD'),
        contractEndDate: dayjs(values.contractEndDate).format('YYYY-MM-DD'),
        tenantPersonalNumber:
          values.tenantPersonalNumber?.replace('-', '') ?? '',
        createdBy: auth.currentUser.uid,
        ownedBy: housing.ownedBy || auth.currentUser.uid,
        status: ApplicationStatus.PENDING,
        tenantAccepted: false,
        apartmentNumber: housing.apartmentNumber || '',
        housingCooperative: housing.housingCooperative || '',
        associationId: housing.associationId || '',
        address: housing.address || values.address,
        city: housing.city || values.city,
        zipCode: housing.zipCode || values.zipCode,
        squareMeter: values.squareMeter || 0,
        rooms: values.rooms || 0,
        step
      };

      console.log('📦 Final application data to submit:', applicationData);

      try {
        // Check for existing applications for this housing
        const existingApplications = await getApplications(auth.currentUser.uid);

        // Spärra bara när PERIODEN krockar med en annan ansökan eller uthyrning på
        // samma lägenhet. (Den här kontrollen läste förr `.data` på en lista och
        // kördes därför aldrig.)
        const blocking = findBlockingApplication(existingApplications || [], applicationData);
        if (blocking) {
          const { start: blockedFrom, end: blockedTo } = rentalPeriod(blocking);
          alert(
            `Du har redan en ansökan eller uthyrning för den här lägenheten ${blockedFrom || '?'} – ${blockedTo || 'utan slutdatum'}. ` +
            (blockedTo ? `Välj ett startdatum efter ${blockedTo}.` : 'Kontakta Hyria om du vill ändra den.')
          );
          return;
        }

        // Wait for the application to be successfully created
        const result = await makeApplicationDirectly(applicationData);
        console.log('✅ Application created successfully with ID:', result.id);

        // Only show the modal after successful submission
        if (result && result.id) {
          setOpen(true);
        } else {
          throw new Error('Ansökan skapades men utan ID');
        }
      } catch (error) {
        console.error('❌ Error creating application:', error);
        const errorMessage = error?.message || 'Ett fel uppstod när ansökan skulle skickas.';
        alert(`Fel: ${errorMessage}`);
      }
      return;
    }

    if (step === 2) {
      // Trigger validation and check for errors
      if (triggerStep2Validation && !triggerStep2Validation()) {
        // Scroll to first error
        setTimeout(() => {
          const firstError = document.querySelector('[data-error="true"], .Mui-error');
          if (firstError) {
            firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 100);
        return;
      }

      // Uppdatera befintlig ansökan med hyresgästuppgifterna
      try {
        // Use callable function to update application and initiate consent request
        const { makeApplicationInitStepTwo } = await import('../../api/application');

        const applicationData = {
          id: values.id,
          // Tenant information
          tenantEmail: values.tenantEmail,
          tenantPersonalNumber: values.tenantPersonalNumber,
          tenantFirstName: values.tenantFirstName,
          tenantLastName: values.tenantLastName,
          tenantPhoneNumber: values.tenantPhoneNumber,
          // Apartment information
          squareMeter: values.squareMeter,
          rooms: values.rooms,
          // Contract period
          contractStartDate: dayjs(values.contractStartDate).format('YYYY-MM-DD'),
          contractEndDate: dayjs(values.contractEndDate).format('YYYY-MM-DD'),
          // Economic information
          rentalFee: values.rentalFee,
          deposit: values.deposit,
          clearingnummer: values.clearingnummer,
          bankAccountNumber: values.bankAccountNumber,
          bankgiro: values.bankgiro,
          // Included services
          heatingIncluded: values.heatingIncluded || false,
          hotWaterIncluded: values.hotWaterIncluded || false,
          electricityIncluded: values.electricityIncluded || false,
          internetIncluded: values.internetIncluded || false,
          parkingIncluded: values.parkingIncluded || false,
          furnitureIncluded: values.furnitureIncluded || false,
          // Miscellaneous
          miscellaneousNotes: values.miscellaneousNotes || ''
        };

        await makeApplicationInitStepTwo(applicationData);

        console.log('✅ Application updated and consent email sent to tenant');
        setOpen(true);
      } catch (error) {
        console.error('❌ Error updating application:', error);
        alert('Ett fel uppstod när ansökan skulle uppdateras.');
      }
      return;
    }
  };

  const isButtonDisabled = () => {
    if (isLoading) return true;

    // Step 1 validation
    if (step === 1) {
      if (!values.selectedApartment || !values.reason) {
        return true;
      }
      if (values.reason === sublettingReasons[6].value && !otherReason) {
        return true;
      }
      return false;
    }

    // Step 2 validation - använd CreateStep2:s validering
    if (step === 2) {
      return step2HasErrors;
    }

    return false;
  };

  const handleModalClose = () => {
    setOpen(false);
    navigate('/applications');
  };

  return (
    <>
      <ApplicationSentModal
        open={open}
        onClose={handleModalClose}
        subtitle={`application_sent_modal_text_${step}`}
      />

      {step === 1 && (
        <CreateStep1V3
          values={values}
          housingList={housingList}
          associationsList={associationsList}
          availableApartments={availableApartments}
          handleChange={handleChange}
          reasons={sublettingReasons}
          setOtherReason={setOtherReason}
          otherReason={otherReason}
          onAssociationChange={handleAssociationChange}
          submitButton={
            <Button
              backgroundColor="#62B8E9"
              textcolor={COLORS.white}
              bordercolor="#62B8E9"
              onClick={handleSubmit}
              disabled={isButtonDisabled()}
              isLoading={isLoading}
              sx={{
                px: 3,
                py: 1.5,
                fontSize: '16px',
                fontWeight: 600,
                borderRadius: '12px',
                textTransform: 'none'
              }}
            >
              {t('create_application')}
            </Button>
          }
        />
      )}
      {step === 2 && (
        <Box sx={{
          maxWidth: 700,
          mx: 'auto',
          px: { xs: 2, sm: 3, md: 0 }
        }}>
          <CreateStep2
            values={values}
            handleChange={handleChange}
            onValidationChange={(hasErrors, triggerValidation) => {
              setStep2HasErrors(hasErrors);
              if (triggerValidation) {
                setTriggerStep2Validation(() => triggerValidation);
              }
            }}
            rentalPeriodStart={
              values.fromDate
                ? (typeof values.fromDate === 'string'
                    ? values.fromDate
                    : values.fromDate.format('YYYY-MM-DD'))
                : null
            }
            rentalPeriodEnd={
              values.toDate
                ? (typeof values.toDate === 'string'
                    ? values.toDate
                    : values.toDate.format('YYYY-MM-DD'))
                : null
            }
            rentalPeriodLoading={applicationLoading}
          />

          <Box sx={{
            mt: 3,
            mb: 3,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 1
          }}>
            <Button
              backgroundColor="#62B8E9"
              textcolor={COLORS.white}
              bordercolor="#62B8E9"
              onClick={handleSubmit}
              disabled={isButtonDisabled()}
              isLoading={isLoading}
            >
              {t('register')}
            </Button>
            {step2HasErrors && (
              <Typography sx={{ color: '#dc2626', fontSize: '14px', textAlign: 'center' }}>
                Fyll i alla obligatoriska fält korrekt för att fortsätta
              </Typography>
            )}
          </Box>
        </Box>
      )}

      {/* Bekräftelsedialog för uthyrningsperiod under 180 dagar */}
      <Dialog
        open={showDateConfirmDialog}
        onClose={handleDateCorrect}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Kontrollera uthyrningsperiod</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Uthyrningsperioden är kortare än 180 dagar ({dayjs(values.toDate).diff(dayjs(values.fromDate), 'day')} dagar).
            Vänligen kontrollera att datumen stämmer överens.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleDateCorrect}
            backgroundColor={COLORS.white}
            textcolor={COLORS.primary}
            bordercolor={COLORS.primary}
          >
            Korrigera
          </Button>
          <Button
            onClick={handleDateConfirm}
            backgroundColor="#62B8E9"
            textcolor={COLORS.white}
            bordercolor="#62B8E9"
          >
            Bekräfta
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Create;
